import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { apiRequest } from "@/lib/queryClient";

interface ProviderCard {
  providerId: string;
  providerKey: string;
  displayName: string;
  writeBackEnabled: boolean;
  statusBadge: "connected" | "not_connected" | "error" | "expiring_soon";
  connectionId: string | null;
  connectionStatus: string | null;
  connectedAt: string | null;
  tokenExpiresAt: string | null;
  lastWriteBack: string | null;
  lastError: string | null;
}

function timeAgo(iso: string | null): string {
  if (!iso) return "Never";
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function expiresIn(iso: string | null): string {
  if (!iso) return "Unknown";
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return "Expired";
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

const badgeStyle = {
  connected: { background: "#1a3a2a", color: "#4ade80", border: "1px solid #16a34a" },
  not_connected: { background: "#1a1a2e", color: "#94a3b8", border: "1px solid #334155" },
  error: { background: "#2a1a1a", color: "#f87171", border: "1px solid #dc2626" },
  expiring_soon: { background: "#2a2010", color: "#fbbf24", border: "1px solid #d97706" },
};

const badgeLabel = {
  connected: "Connected",
  not_connected: "Not connected",
  error: "Error",
  expiring_soon: "Token expiring",
};

export default function SettingsIntegrations() {
  const [providers, setProviders] = useState<ProviderCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [disconnecting, setDisconnecting] = useState<string | null>(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState<string | null>(null);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);
  const [location] = useLocation();

  const params = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
  const connectedOk = params.get("connected") === "true";
  const connError = params.get("error");

  async function load() {
    try {
      const r = await apiRequest("GET", "/api/ats/providers");
      if (!r.ok) {
        setError("Failed to load integrations");
        return;
      }
      const data = await r.json();
      setProviders(data.providers || []);
    } catch {
      setError("Network error loading integrations");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function confirmAndDisconnect() {
    if (!confirmDisconnect) return;
    setDisconnecting(confirmDisconnect);
    setDisconnectError(null);
    try {
      const r = await apiRequest("DELETE", "/api/settings/ats/disconnect");
      if (r.ok) {
        setConfirmDisconnect(null);
        await load();
      } else {
        const data = await r.json().catch(() => ({}));
        setDisconnectError((data as any).error || "Disconnect failed. Please try again.");
      }
    } catch {
      setDisconnectError("Network error — please check your connection and try again.");
    } finally {
      setDisconnecting(null);
    }
  }

  const totalConnected = providers.filter((p) => p.statusBadge === "connected" || p.statusBadge === "expiring_soon").length;
  const hasErrors = providers.some((p) => p.statusBadge === "error");
  const nextExpiry = providers
    .filter((p) => p.tokenExpiresAt)
    .sort((a, b) => new Date(a.tokenExpiresAt!).getTime() - new Date(b.tokenExpiresAt!).getTime())[0];

  const page: React.CSSProperties = {
    minHeight: "100vh",
    background: "#05070a",
    color: "#e2e8f0",
    fontFamily: "'Inter', system-ui, sans-serif",
    padding: "40px 24px",
  };

  const container: React.CSSProperties = {
    maxWidth: 900,
    margin: "0 auto",
  };

  const heading: React.CSSProperties = {
    fontSize: 28,
    fontWeight: 700,
    color: "#f8fafc",
    marginBottom: 6,
  };

  const sub: React.CSSProperties = {
    color: "#94a3b8",
    fontSize: 14,
    marginBottom: 32,
  };

  const summaryBar: React.CSSProperties = {
    display: "flex",
    gap: 24,
    padding: "16px 20px",
    background: "#0d1117",
    border: "1px solid rgba(99,102,241,0.2)",
    borderRadius: 10,
    marginBottom: 28,
    flexWrap: "wrap",
  };

  const summaryItem: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  };

  const summaryLabel: React.CSSProperties = {
    fontSize: 11,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  };

  const summaryValue: React.CSSProperties = {
    fontSize: 18,
    fontWeight: 600,
    color: "#f8fafc",
  };

  const card: React.CSSProperties = {
    background: "#0d1117",
    border: "1px solid rgba(99,102,241,0.15)",
    borderRadius: 12,
    padding: "24px",
    marginBottom: 16,
  };

  const btn = (variant: "primary" | "ghost" | "danger"): React.CSSProperties => ({
    padding: "8px 16px",
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    border: variant === "ghost" ? "1px solid rgba(255,255,255,0.15)" : "none",
    background:
      variant === "primary"
        ? "#6366f1"
        : variant === "danger"
        ? "rgba(220,38,38,0.15)"
        : "transparent",
    color:
      variant === "danger"
        ? "#f87171"
        : "#f8fafc",
  });

  if (loading) {
    return (
      <div style={page}>
        <div style={container}>
          <div style={{ color: "#94a3b8", fontSize: 14 }}>Loading integrations...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={page}>
      <div style={container}>
        <div style={{ marginBottom: 24 }}>
          <a
            href="/dashboard?tab=settings"
            style={{ color: "#6366f1", fontSize: 13, fontWeight: 500, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}
            data-testid="link-back-to-dashboard"
          >
            ← Back to dashboard
          </a>
        </div>
        <h1 style={heading}>Integration Settings</h1>
        <p style={sub}>Manage your connections to external platforms. All tokens are encrypted at rest.</p>

        {connectedOk && (
          <div style={{ padding: "12px 16px", background: "#1a3a2a", border: "1px solid #16a34a", borderRadius: 8, marginBottom: 20, color: "#4ade80", fontSize: 14 }}
            data-testid="banner-connected-success">
            Integration connected successfully.
          </div>
        )}
        {connError && (
          <div style={{ padding: "12px 16px", background: "#2a1a1a", border: "1px solid #dc2626", borderRadius: 8, marginBottom: 20, color: "#f87171", fontSize: 14 }}
            data-testid="banner-connected-error">
            {connError === "workspace_not_configured"
              ? "Your workspace is not fully set up. Please log out and log back in, or contact support if this persists."
              : `Connection error: ${connError.replace(/_/g, " ")}. Please try again.`}
          </div>
        )}
        {error && (
          <div style={{ padding: "12px 16px", background: "#2a1a1a", border: "1px solid #dc2626", borderRadius: 8, marginBottom: 20, color: "#f87171", fontSize: 14 }}
            data-testid="banner-load-error">
            {error}
          </div>
        )}

        <div style={summaryBar} data-testid="integration-health-summary">
          <div style={summaryItem}>
            <span style={summaryLabel}>Connected</span>
            <span style={summaryValue}>{totalConnected} / {providers.length}</span>
          </div>
          <div style={summaryItem}>
            <span style={summaryLabel}>Errors</span>
            <span style={{ ...summaryValue, color: hasErrors ? "#f87171" : "#4ade80" }}>
              {hasErrors ? "Yes" : "None"}
            </span>
          </div>
          <div style={summaryItem}>
            <span style={summaryLabel}>Next token expiry</span>
            <span style={summaryValue}>
              {nextExpiry?.tokenExpiresAt ? expiresIn(nextExpiry.tokenExpiresAt) : "N/A"}
            </span>
          </div>
        </div>

        {providers.length === 0 && (
          <div style={{ ...card, textAlign: "center", color: "#64748b", fontSize: 14, padding: 40 }}>
            No active integrations configured.
          </div>
        )}

        {providers.map((p) => (
          <div key={p.providerId} style={card} data-testid={`integration-card-${p.providerId}`}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
                  <span style={{ fontSize: 17, fontWeight: 600, color: "#f8fafc" }} data-testid={`integration-name-${p.providerId}`}>
                    {p.displayName}
                  </span>
                  <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 500, ...badgeStyle[p.statusBadge] }}
                    data-testid={`integration-badge-${p.providerId}`}>
                    {badgeLabel[p.statusBadge]}
                  </span>
                </div>
                {(p.statusBadge === "connected" || p.statusBadge === "expiring_soon") && (
                  <div style={{ fontSize: 12, color: "#64748b", display: "flex", gap: 20, flexWrap: "wrap" }}>
                    <span>Connected {timeAgo(p.connectedAt)}</span>
                    <span style={{ color: p.statusBadge === "expiring_soon" ? "#fbbf24" : undefined }}>
                      Token expires in {expiresIn(p.tokenExpiresAt)}
                    </span>
                    {p.lastWriteBack && <span>Last write-back {timeAgo(p.lastWriteBack)}</span>}
                  </div>
                )}
                {p.statusBadge === "error" && p.lastError && (
                  <div style={{ fontSize: 12, color: "#f87171", marginTop: 4 }} data-testid={`integration-error-${p.providerId}`}>
                    Error: {p.lastError}
                  </div>
                )}
                {p.statusBadge === "not_connected" && (
                  <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }} data-testid={`integration-hint-${p.providerId}`}>
                    You must connect {p.displayName} before write-backs will work.
                  </div>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
                {(p.statusBadge === "connected" || p.statusBadge === "expiring_soon") ? (
                  confirmDisconnect === p.providerId ? (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
                      <p style={{ fontSize: 13, color: "#94a3b8", maxWidth: 320, textAlign: "right", margin: 0 }}
                         data-testid={`text-disconnect-confirm-${p.providerId}`}>
                        This will remove Vexis access to your JobAdder account. Candidate assessments already in progress will not be affected.
                      </p>
                      {disconnectError && (
                        <p style={{ fontSize: 12, color: "#f87171", margin: 0 }}
                           data-testid={`text-disconnect-error-${p.providerId}`}>
                          {disconnectError}
                        </p>
                      )}
                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          style={btn("ghost")}
                          onClick={() => { setConfirmDisconnect(null); setDisconnectError(null); }}
                          disabled={disconnecting === p.providerId}
                          data-testid={`button-disconnect-cancel-${p.providerId}`}>
                          Cancel
                        </button>
                        <button
                          style={btn("danger")}
                          onClick={confirmAndDisconnect}
                          disabled={disconnecting === p.providerId}
                          data-testid={`button-disconnect-confirm-${p.providerId}`}>
                          {disconnecting === p.providerId ? "Disconnecting..." : "Yes, disconnect"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      style={btn("danger")}
                      onClick={() => { setConfirmDisconnect(p.providerId); setDisconnectError(null); }}
                      data-testid={`button-disconnect-${p.providerId}`}>
                      Disconnect
                    </button>
                  )
                ) : (
                  <a href={`/auth/ats/${p.providerKey}`}
                    style={{ ...btn("primary"), textDecoration: "none", display: "inline-block" }}
                    data-testid={`button-connect-${p.providerId}`}>
                    Connect {p.displayName}
                  </a>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
